// Система логирования и аудита действий
export interface AuditEvent {
  id: string;
  timestamp: Date;
  userId: string;
  sessionId: string;
  action: string;
  category: 'user_action' | 'system_event' | 'security_event' | 'data_access' | 'calculation' | 'error';
  severity: 'info' | 'warning' | 'error' | 'critical';
  details: {
    description: string;
    data?: any;
    ipAddress?: string;
    userAgent?: string;
    location?: string;
  };
  compliance: boolean;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  tags: string[];
}

export interface AuditQuery {
  userId?: string;
  category?: AuditEvent['category'];
  severity?: AuditEvent['severity'];
  riskLevel?: AuditEvent['riskLevel'];
  startDate?: Date;
  endDate?: Date;
  action?: string;
  compliance?: boolean;
}

export interface AuditStats {
  totalEvents: number;
  byCategory: Record<string, number>;
  bySeverity: Record<string, number>;
  byRiskLevel: Record<string, number>;
  complianceRate: number;
  errorRate: number;
  lastActivity: Date | null;
  topActions: Array<{ action: string; count: number }>;
}

// Менеджер аудита
export class AuditLogger {
  private static instance: AuditLogger;
  private events: AuditEvent[] = [];
  private sessionId: string;

  private constructor() {
    this.sessionId = this.generateSessionId();
  }

  public static getInstance(): AuditLogger {
    if (!AuditLogger.instance) {
      AuditLogger.instance = new AuditLogger();
    }
    return AuditLogger.instance;
  }

  // Логирование события
  public logEvent(event: Omit<AuditEvent, 'id' | 'timestamp' | 'sessionId'>): string {
    const auditEvent: AuditEvent = {
      ...event,
      id: this.generateId(),
      timestamp: new Date(),
      sessionId: this.sessionId
    };

    this.events.unshift(auditEvent);
    
    // Ограничиваем количество событий (последние 10000)
    if (this.events.length > 10000) {
      this.events = this.events.slice(0, 10000);
    }

    // Сохраняем в localStorage для персистентности
    this.saveToStorage();

    return auditEvent.id;
  }

  // Логирование пользовательских действий
  public logUserAction(
    userId: string,
    action: string,
    details: string,
    data?: any,
    riskLevel: AuditEvent['riskLevel'] = 'low'
  ): string {
    return this.logEvent({
      userId,
      action,
      category: 'user_action',
      severity: 'info',
      details: {
        description: details,
        data,
        ipAddress: this.getClientIP(),
        userAgent: navigator.userAgent,
        location: window.location.href
      },
      compliance: true,
      riskLevel,
      tags: ['user_action', action.toLowerCase().replace(/\s+/g, '_')]
    });
  }

  // Логирование системных событий
  public logSystemEvent(
    action: string,
    details: string,
    severity: AuditEvent['severity'] = 'info',
    data?: any
  ): string {
    return this.logEvent({
      userId: 'system',
      action,
      category: 'system_event',
      severity,
      details: {
        description: details,
        data,
        ipAddress: this.getClientIP(),
        userAgent: navigator.userAgent,
        location: window.location.href
      },
      compliance: true,
      riskLevel: severity === 'error' || severity === 'critical' ? 'high' : 'low',
      tags: ['system_event', action.toLowerCase().replace(/\s+/g, '_')]
    });
  }

  // Логирование событий безопасности
  public logSecurityEvent(
    userId: string,
    action: string,
    details: string,
    severity: AuditEvent['severity'] = 'warning',
    data?: any
  ): string {
    return this.logEvent({
      userId,
      action,
      category: 'security_event',
      severity,
      details: {
        description: details,
        data,
        ipAddress: this.getClientIP(),
        userAgent: navigator.userAgent,
        location: window.location.href
      },
      compliance: false,
      riskLevel: severity === 'critical' ? 'critical' : 'high',
      tags: ['security', action.toLowerCase().replace(/\s+/g, '_')]
    });
  }

  // Логирование доступа к данным
  public logDataAccess(
    userId: string,
    dataType: string,
    action: 'read' | 'write' | 'delete' | 'export',
    details: string,
    data?: any
  ): string {
    return this.logEvent({
      userId,
      action: `${action}_${dataType}`,
      category: 'data_access',
      severity: 'info',
      details: {
        description: details,
        data,
        ipAddress: this.getClientIP(),
        userAgent: navigator.userAgent,
        location: window.location.href
      },
      compliance: true,
      riskLevel: action === 'delete' ? 'high' : 'low',
      tags: ['data_access', dataType, action]
    });
  }

  // Логирование расчетов
  public logCalculation(
    userId: string,
    calculationType: string,
    input: any,
    result: any,
    validationPassed: boolean
  ): string {
    return this.logEvent({
      userId,
      action: `calculation_${calculationType}`,
      category: 'calculation',
      severity: validationPassed ? 'info' : 'warning',
      details: {
        description: `Выполнен расчет: ${calculationType}`,
        data: { input, result, validationPassed },
        ipAddress: this.getClientIP(),
        userAgent: navigator.userAgent,
        location: window.location.href
      },
      compliance: validationPassed,
      riskLevel: validationPassed ? 'low' : 'medium',
      tags: ['calculation', calculationType]
    });
  }

  // Логирование ошибок
  public logError(
    userId: string,
    error: Error,
    context: string,
    data?: any
  ): string {
    return this.logEvent({
      userId,
      action: 'error_occurred',
      category: 'error',
      severity: 'error',
      details: {
        description: `Ошибка в ${context}: ${error.message}`,
        data: { 
          error: error.stack, 
          context, 
          ...data 
        },
        ipAddress: this.getClientIP(),
        userAgent: navigator.userAgent,
        location: window.location.href
      },
      compliance: false,
      riskLevel: 'high',
      tags: ['error', context.toLowerCase().replace(/\s+/g, '_')]
    });
  }

  // Поиск событий
  public searchEvents(query: AuditQuery, limit: number = 100): AuditEvent[] {
    let filteredEvents = this.events;

    if (query.userId) {
      filteredEvents = filteredEvents.filter(event => event.userId === query.userId);
    }

    if (query.category) {
      filteredEvents = filteredEvents.filter(event => event.category === query.category);
    }

    if (query.severity) {
      filteredEvents = filteredEvents.filter(event => event.severity === query.severity);
    }

    if (query.riskLevel) {
      filteredEvents = filteredEvents.filter(event => event.riskLevel === query.riskLevel);
    }

    if (query.startDate) {
      filteredEvents = filteredEvents.filter(event => event.timestamp >= query.startDate!);
    }

    if (query.endDate) {
      filteredEvents = filteredEvents.filter(event => event.timestamp <= query.endDate!);
    }

    if (query.action) {
      filteredEvents = filteredEvents.filter(event => 
        event.action.toLowerCase().includes(query.action!.toLowerCase())
      );
    }

    if (query.compliance !== undefined) {
      filteredEvents = filteredEvents.filter(event => event.compliance === query.compliance);
    }

    return filteredEvents.slice(0, limit);
  }

  // Получение статистики
  public getStats(): AuditStats {
    const totalEvents = this.events.length;
    
    const byCategory: Record<string, number> = {};
    const bySeverity: Record<string, number> = {};
    const byRiskLevel: Record<string, number> = {};
    const actionCounts: Record<string, number> = {};
    
    let compliantCount = 0;
    let errorCount = 0;
    let lastActivity: Date | null = null;

    this.events.forEach(event => {
      byCategory[event.category] = (byCategory[event.category] || 0) + 1;
      bySeverity[event.severity] = (bySeverity[event.severity] || 0) + 1;
      byRiskLevel[event.riskLevel] = (byRiskLevel[event.riskLevel] || 0) + 1;
      actionCounts[event.action] = (actionCounts[event.action] || 0) + 1;
      
      if (event.compliance) compliantCount++;
      if (event.severity === 'error' || event.severity === 'critical') errorCount++;
      
      if (!lastActivity || event.timestamp > lastActivity) {
        lastActivity = event.timestamp;
      }
    });

    const topActions = Object.entries(actionCounts)
      .map(([action, count]) => ({ action, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return {
      totalEvents,
      byCategory,
      bySeverity,
      byRiskLevel,
      complianceRate: totalEvents > 0 ? Math.round((compliantCount / totalEvents) * 100) : 0,
      errorRate: totalEvents > 0 ? Math.round((errorCount / totalEvents) * 100) : 0,
      lastActivity,
      topActions
    };
  }

  // Экспорт логов
  public exportLogs(query?: AuditQuery): string {
    const events = query ? this.searchEvents(query) : this.events;
    
    const csvHeader = 'ID,Timestamp,UserID,Action,Category,Severity,Compliance,RiskLevel,Description,IP,UserAgent,Location\n';
    const csvRows = events.map(event => 
      `${event.id},${event.timestamp.toISOString()},${event.userId},${event.action},${event.category},${event.severity},${event.compliance},${event.riskLevel},"${event.details.description}","${event.details.ipAddress}","${event.details.userAgent}","${event.details.location}"`
    ).join('\n');
    
    return csvHeader + csvRows;
  }

  // Очистка старых логов
  public cleanupOldLogs(daysToKeep: number = 30): number {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
    
    const initialCount = this.events.length;
    this.events = this.events.filter(event => event.timestamp >= cutoffDate);
    const removedCount = initialCount - this.events.length;
    
    this.saveToStorage();
    return removedCount;
  }

  // Получение IP адреса клиента (заглушка)
  private getClientIP(): string {
    // В реальном приложении это будет получаться с сервера
    return '127.0.0.1';
  }

  // Генерация ID сессии
  private generateSessionId(): string {
    return 'session_' + Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Генерация ID события
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Сохранение в localStorage
  private saveToStorage(): void {
    try {
      const recentEvents = this.events.slice(0, 1000); // Сохраняем только последние 1000 событий
      localStorage.setItem('audit_logs', JSON.stringify(recentEvents));
    } catch (error) {
      console.error('Ошибка сохранения логов аудита:', error);
    }
  }

  // Загрузка из localStorage
  public loadFromStorage(): void {
    try {
      const stored = localStorage.getItem('audit_logs');
      if (stored) {
        const events = JSON.parse(stored).map((event: any) => ({
          ...event,
          timestamp: new Date(event.timestamp)
        }));
        this.events = events;
      }
    } catch (error) {
      console.error('Ошибка загрузки логов аудита:', error);
    }
  }
}

// Экспорт глобального экземпляра
export const auditLogger = AuditLogger.getInstance();

// Инициализация при загрузке
auditLogger.loadFromStorage();
